import type { CallbackInterface } from "recoil";

import type { ModalBulkRemoveFromGroupsState } from "Pages/RecipientsList/model";

import {
	notifyBulkRemoveFromGroups,
	removeFromGroups,
} from "Pages/RecipientsList/recoil/services/modalBulkRemoveFromGroups";
import { addGroup } from "Pages/RecipientsList/recoil/services/modals";
import {
	appliedSearchFiltersState,
	searchGroupsInState,
} from "Pages/RecipientsList/recoil/state/filters";
import { modalBulkRemoveFromGroupsState } from "Pages/RecipientsList/recoil/state/modals";
import { selectionState } from "Pages/RecipientsList/recoil/state/results";

import type { PageStateType } from "Types/state";

import { pageState } from "State/page";

import { logAppError } from "Helpers/logger";

import getIntl from "Helpers/intl";
import { gainsightTrack } from "mailup-react-helpers";

export const modalBulkRemoveFromGroupsOpenCallback =
	({ set, snapshot }: CallbackInterface) =>
	async () => {
		gainsightTrack("mup_recipients_click_removeFromGroupActionBulk", {});
		const searchGroupsIn = await snapshot.getPromise(searchGroupsInState);

		set<ModalBulkRemoveFromGroupsState>(
			modalBulkRemoveFromGroupsState,
			(prev) => ({
				...prev,
				show: true,
				searchGroups: [...searchGroupsIn],
			}),
		);
	};

export const modalBulkRemoveFromGroupsCancelCallback =
	({ reset }: CallbackInterface) =>
	async () => {
		reset(modalBulkRemoveFromGroupsState);
	};

export const modalBulkRemoveFromGroupsChangeCallback =
	({ set }: CallbackInterface) =>
	async (p: Partial<ModalBulkRemoveFromGroupsState>) => {
		set(modalBulkRemoveFromGroupsState, (prev) => ({
			...prev,
			...p,
		}));
	};

export const modalBulkRemoveFromGroupsToggleOptMoveRandomCallback =
	({ set }: CallbackInterface) =>
	async () => {
		set<ModalBulkRemoveFromGroupsState>(
			modalBulkRemoveFromGroupsState,
			(prev) => ({
				...prev,
				optMoveRandom: !prev.optMoveRandom,
			}),
		);
	};

export const modalBulkRemoveFromGroupsToggleOptAddToGroupCallback =
	({ set }: CallbackInterface) =>
	async () => {
		set<ModalBulkRemoveFromGroupsState>(
			modalBulkRemoveFromGroupsState,
			(prev) => ({
				...prev,
				optAddToGroup: !prev.optAddToGroup,
			}),
		);
	};

export const modalBulkRemoveFromGroupsToggleOptSetAsTestGroupCallback =
	({ set }: CallbackInterface) =>
	async () => {
		set<ModalBulkRemoveFromGroupsState>(
			modalBulkRemoveFromGroupsState,
			(prev) => ({
				...prev,
				optSetAsTestGroup: !prev.optSetAsTestGroup,
			}),
		);
	};

export const modalBulkRemoveFromGroupsAddGroupCallback =
	({ set, snapshot }: CallbackInterface) =>
	async (searchTerm: string) => {
		try {
			const newItem = await addGroup(searchTerm);

			set(modalBulkRemoveFromGroupsState, (prev) => ({
				...prev,
				addToGroupId: newItem.Id,
			}));
		} catch (error) {
			console.error("### error", error);
		}
	};

export const modalBulkRemoveFromGroupsConfirmCallback =
	({ snapshot, set, reset }: CallbackInterface) =>
	async () => {
		set(pageState, (prev) => ({
			...prev,
			loading: true,
		}));

		const [
			{ mode, checked },
			searchFilters,
			{
				addToGroupId,
				optAddToGroup,

				removeFromGroupsMode,
				optMoveRandom,
				moveRandomAmount,
				optSetAsTestGroup,
				searchGroups,
			},
		] = await Promise.all([
			snapshot.getPromise(selectionState),
			snapshot.getPromise(appliedSearchFiltersState),
			snapshot.getPromise(modalBulkRemoveFromGroupsState),
		]);

		try {
			await removeFromGroups(
				{
					mode,
					checked,
					searchFilters,
				},
				{
					addToGroupId,
					optAddToGroup,
					removeFromGroupsMode,
					optMoveRandom,
					moveRandomAmount,
					optSetAsTestGroup,
					searchGroups,
				},
			);

			reset(modalBulkRemoveFromGroupsState);

			set(pageState, (prev) => ({
				...prev,
				loading: false,
			}));

			notifyBulkRemoveFromGroups(optAddToGroup);
		} catch (error) {
			console.error("### error", error);

			logAppError({
				Component: "RecipientsList",
				Method: "modalBulkRemoveFromGroupsConfirmCallback",
				appError: error,
			});

			set<PageStateType>(pageState, (prev) => ({
				...prev,
				status: "error",
				loading: false,
				errorMessage: getIntl().formatMessage({
					id: "Load.Alerts.ErrorGeneric",
				}),
			}));
		}
	};
